/* eslint-disable import/prefer-default-export */
import { FieldFunctionOptions, FieldPolicy, Reference } from '@apollo/client'

import {
  Filter,
  FilterMap,
  getCacheKey,
  getFilters,
  getNewIncomingData,
  getPaginatedData,
  getFilteredData,
} from '../utils'

import { KpiObj, ObjectiveFilterInput, ObjectiveObj, OkrType } from '~generated/types'

type IncomingObjectives = {
  docs: Reference[]
  totalPages: number
  totalDocs: number
  page: number
}

type ExistingObjectives = {
  docs: Reference[]
} & {
  [key: string]: Pick<IncomingObjectives, 'totalPages' | 'totalDocs' | 'page'>
}

export class ObjectivesQueryCache implements FieldPolicy {
  keyArgs: false

  merge = (
    existing: ExistingObjectives,
    incoming: IncomingObjectives,
    { variables, readField, canRead }: FieldFunctionOptions
  ) => {
    const { filter } = variables
    const { docs: existingDocs = [], ...restExisting } = existing || {}
    const { docs: incomingDocs = [], ...restIncoming } = incoming

    const newIncomingDocs = getNewIncomingData({ incomingData: incomingDocs, existingData: existingDocs, readField })

    // const docs = [...existingDocs.filter(canRead), ...newIncomingDocs]
    const docs = [...existingDocs.filter(canRead), ...newIncomingDocs].sort((docA, docB) => {
      const timeA = new Date(readField('createdAt', docA) as string).getTime()
      const timeB = new Date(readField('createdAt', docB) as string).getTime()
      return timeB - timeA
    })

    const filterKey = getCacheKey(filter)

    return {
      ...restExisting,
      [filterKey]: restIncoming,
      docs,
    }
  }

  read = (existing, options: FieldFunctionOptions) => {
    if (!existing) return undefined
    const { variables, readField } = options
    const { pagination, filter } = variables
    const filterKey = getCacheKey(filter || {})
    const filters = getFilters(filter, this.filters)

    const paginationData = existing[filterKey]

    const filteredDocs = getFilteredData({ filter, filters, data: existing.docs, readField })
    // const filteredDocs = isEmpty(filter)
    //   ? this.getFilteredDataWithEmptyFilters(existing.docs, readField)
    //   : getFilteredData({ filter, filters, existingData: existing.docs, readField })

    const docs = getPaginatedData({ filteredData: filteredDocs, page: paginationData?.page, limit: pagination?.limit })

    return { ...paginationData, docs }
  }

  get filters() {
    return {
      launchId: this.launchId,
      assigneeIds: this.assigneeIds,
      roadmapIds: this.roadmapIds,
      releaseIds: this.releaseIds,
      type: this.type,
      startDate: this.startDate,
      endDate: this.endDate,
      search: this.search,
    } as FilterMap<ObjectiveFilterInput>
  }

  launchId: Filter<ObjectiveFilterInput> = (ref, filter, readField) => {
    return readField('launchId', ref) === filter.launchId
  }

  assigneeIds: Filter<ObjectiveFilterInput> = (ref, filter, readField) => {
    const key: keyof ObjectiveObj = 'assignee'
    const assignee = readField(key, ref) as Reference
    return filter.assigneeIds.includes(readField('id', assignee))
  }

  roadmapIds: Filter<ObjectiveFilterInput> = (ref, filter, readField) => {
    const key: keyof ObjectiveObj = 'roadmapId'
    const roadmapId = readField(key, ref) as string
    return filter.roadmapIds.includes(roadmapId)
  }

  releaseIds: Filter<ObjectiveFilterInput> = (ref, filter, readField) => {
    const key: keyof ObjectiveObj = 'releaseId'
    const releaseId = readField(key, ref) as string
    return filter.releaseIds.includes(releaseId)
  }

  type: Filter<ObjectiveFilterInput> = (ref, filter, readField) => {
    const key: keyof ObjectiveObj = 'type'
    const type = readField(key, ref) as OkrType
    return filter?.type === type
  }

  startDate: Filter<ObjectiveFilterInput> = (ref, filter, readField) => {
    if (!filter.startDate) return true

    const key: keyof ObjectiveObj = 'dueDate'
    const date = new Date(readField(key, ref))

    const startDate = new Date(filter.startDate)

    return startDate <= date
  }

  endDate: Filter<ObjectiveFilterInput> = (ref, filter, readField) => {
    if (!filter.endDate) return true

    const key: keyof ObjectiveObj = 'dueDate'
    const date = new Date(readField(key, ref))

    const endDate = new Date(filter.endDate)

    return endDate >= date
  }

  search: Filter<ObjectiveFilterInput> = (ref, filter, readField) => {
    const objectiveTitle = readField('title', ref) as string
    const objectiveKpis = readField('kpis', ref) as KpiObj[]
    const searchRegExp = new RegExp(filter.search, 'i')
    return (
      searchRegExp.test(objectiveTitle) || objectiveKpis.some((idRef) => searchRegExp.test(readField('title', idRef)))
    )
  }

  getFilteredDataWithEmptyFilters = (docs, readField) => docs.filter((doc) => readField('launchId', doc) === null)
}
